// src/pages/listing/Listing.jsx
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ShieldCheck,
  MapPin,
  Gauge,
  Ruler,
  Weight,
  Truck,
  Thermometer,
  Clock8,
  Layers,
  Share2,
  Star,
  CalendarDays,
  Phone,
  Mail,
  Building2
} from 'lucide-react';

import { LISTINGS_MOCK } from '../../mocks/listing.mock';
import { formatMXN } from '../../features/listing/utils/money';

import './Listing.css';

const HERO_FALLBACK = 'https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?auto=format&fit=crop&w=1600&q=80';

const DEFAULT_PRECIO = {
  moneda: 'MXN',
  minimo: 0,
  porKm: 0,
};

const VerifiedBadge = () => (
  <span className="verified-badge">
    <ShieldCheck size={16} strokeWidth={1.5} />
    Proveedor verificado
  </span>
);

const Rating = ({ value = 0 }) => {
  const stars = Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      size={18}
      strokeWidth={1.5}
      className={index < Math.round(value) ? 'rating-star rating-star--filled' : 'rating-star'}
    />
  ));

  return (
    <div className="rating">
      <div className="rating-stars">{stars}</div>
      <span className="rating-value">{value?.toFixed(1) ?? '0.0'}</span>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="feature-card">
      <div className="feature-icon">
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div className="feature-copy">
        <p className="feature-label">{label}</p>
        <p className="feature-value">{value}</p>
      </div>
    </div>
  );
};

export default function ListingDetail() {
  const { slug, id } = useParams();

  const listing = useMemo(() => {
    if (slug && !/^\d+$/.test(slug)) {
      const bySlug = LISTINGS_MOCK.find(u => u.slug === slug);
      if (bySlug) return bySlug;
    }
    if (id) {
      const byIdParam = LISTINGS_MOCK.find(u => u.id === Number(id));
      if (byIdParam) return byIdParam;
    }
    if (slug && /^\d+$/.test(slug)) {
      const byNumericSlug = LISTINGS_MOCK.find(u => u.id === Number(slug));
      if (byNumericSlug) return byNumericSlug;
    }
    return undefined;
  }, [slug, id]);

  const priceConfig = listing?.precio ?? DEFAULT_PRECIO;
  const hero = listing?.imagenes?.[0] ?? HERO_FALLBACK;
  const providerHref = listing?.proveedor?.slug ? `/provider/${listing.proveedor.slug}` : '/provider';

  const features = useMemo(() => ([
    { icon: Truck, label: 'Tipo de unidad', value: listing?.unidad?.tipo },
    { icon: Gauge, label: 'Carrocería', value: listing?.unidad?.carroceria },
    { icon: Clock8, label: 'Modelo', value: listing?.unidad?.ano && `Año ${listing.unidad.ano}` },
    {
      icon: Weight,
      label: 'Capacidad',
      value: listing?.unidad?.capacidad?.pesoTon
        ? `${listing.unidad.capacidad.pesoTon} ton`
        : listing?.unidad?.capacidad?.pesoKg
          ? `${listing.unidad.capacidad.pesoKg} kg`
          : null,
    },
    {
      icon: Layers,
      label: 'Volumen útil',
      value: listing?.unidad?.capacidad?.volumenM3 && `${listing.unidad.capacidad.volumenM3} m³`,
    },
    {
      icon: Ruler,
      label: 'Dimensiones',
      value: listing?.unidad?.dimensiones
        ? `${listing.unidad.dimensiones.largoM} × ${listing.unidad.dimensiones.anchoM} × ${listing.unidad.dimensiones.altoM} m`
        : null,
    },
    {
      icon: Thermometer,
      label: 'Refrigeración',
      value: listing?.unidad?.refrigeracion?.disponible
        ? listing.unidad.refrigeracion.rangoC ?? 'Reefer disponible'
        : listing?.unidad?.refrigeracion?.disponible === false
          ? 'Seco'
          : null,
    },
    {
      icon: Clock8,
      label: 'Disponibilidad',
      value: listing?.disponibilidad ?? 'Entrega inmediata',
    },
  ]), [listing]);

  const tariffs = useMemo(() => {
    if (listing?.tarifas?.length) {
      return listing.tarifas.map(route => ({
        destino: route.destino,
        precio: route.precio ? formatMXN(route.precio) : 'Bajo solicitud',
      }));
    }

    const estados = listing?.cobertura?.estados ?? [];
    const fallbackTargets = estados.length ? estados.slice(0, 3) : ['Ruta principal', 'Ruta extendida', 'Ruta dedicada'];
    const price = listing?.precio ?? DEFAULT_PRECIO;

    return fallbackTargets.map((target, index) => {
      const amount = price.minimo + price.porKm * (180 + index * 60);
      return {
        destino: `${listing?.baseOperacion ?? 'Origen'} → ${target}`,
        precio: amount ? formatMXN(amount) : 'Bajo solicitud',
      };
    });
  }, [listing]);

  const description = listing?.descripcion ?? (
    `Unidad ${listing?.unidad?.tipo ?? 'especializada'} lista para rutas ${
      listing?.cobertura?.nacional ? 'nacionales' : 'regionales'
    }, operada desde ${listing?.baseOperacion ?? 'nuestro hub principal'}. Ideal para cargas ${
      listing?.categoria ?? 'comerciales'
    } con monitoreo constante y equipo verificado.`
  );

  if (!listing) {
    return (
      <div className="listing-page">
        <div className="listing-container">
          <p className="empty">Unidad no encontrada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="listing-page">
      <div className="listing-container">
        <nav className="listing-breadcrumbs" aria-label="breadcrumb">
          <Link to="/">Inicio</Link>
          <span aria-hidden="true">/</span>
          <Link to="/marketplace">Marketplace</Link>
          <span aria-hidden="true">/</span>
          <span>{listing.categoria}</span>
        </nav>

        <div className="listing-grid">
          <div className="listing-main">
            <div className="hero-media">
              <img src={hero} alt={listing.titulo} loading="lazy" />
            </div>

            <header className="listing-header">
              <div>
                <p className="listing-pill">{listing.categoria}</p>
                <h1>{listing.titulo}</h1>
              </div>
              <button type="button" className="icon-button">
                <Share2 size={18} strokeWidth={1.5} />
                Compartir
              </button>
            </header>

            <div className="listing-subheader">
              <span className="location">
                <MapPin size={18} strokeWidth={1.5} />
                {listing.baseOperacion}
              </span>
              {listing.proveedor?.verificado && <VerifiedBadge />}
              <Rating value={listing.rating ?? 0} />
            </div>

            <section className="feature-grid">
              {features.map(feature => (
                <FeatureCard key={feature.label} {...feature} />
              ))}
            </section>

            <section className="narrative-card">
              <h2>Descripción</h2>
              <p>{description}</p>
            </section>

            <section className="narrative-card">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Tarifas referenciales</p>
                  <h2>Destino vs precio estimado</h2>
                </div>
                <span className="tariff-hint">Basado en rutas recientes</span>
              </div>

              <div className="tariff-table">
                <div className="tariff-row tariff-header">
                  <span>Destino</span>
                  <span>Precio referencial</span>
                </div>
                {tariffs.map((row, index) => (
                  <div className="tariff-row" key={`${row.destino}-${index}`}>
                    <span>{row.destino}</span>
                    <span>{row.precio}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="listing-sidebar" aria-label="Acciones rápidas">
            <div className="booking-card">
              <div className="status-pill">Disponible</div>
              <p className="booking-label">Precio desde</p>
              <p className="booking-price">{formatMXN(priceConfig.minimo)}</p>
              <p className="booking-meta">
                {priceConfig.porKm ? `${formatMXN(priceConfig.porKm)} / km` : 'Tarifa personalizada'}
              </p>

              <button type="button" className="date-picker">
                <div>
                  <span>Seleccionar fechas</span>
                  <small>Define ventana estimada</small>
                </div>
                <CalendarDays size={20} strokeWidth={1.5} />
              </button>

              <button type="button" className="primary-action">
                Solicitar Cotización
              </button>

              <p className="secure-note">No se hará ningún cargo todavía.</p>
            </div>

            <div className="host-card">
              <div className="host-meta">
                <div className="host-logo">
                  {listing.proveedor?.logo ? (
                    <img src={listing.proveedor.logo} alt={listing.proveedor.nombre} />
                  ) : (
                    <span>{listing.proveedor?.nombre?.charAt(0) ?? 'F'}</span>
                  )}
                </div>
                <div>
                  <p className="eyebrow">Proveedor</p>
                  <p className="host-name">{listing.proveedor?.nombre}</p>
                  <p className="host-base">
                    <Building2 size={16} strokeWidth={1.4} />
                    {listing.proveedor?.baseOperacion ?? listing.baseOperacion}
                  </p>
                </div>
              </div>

              <div className="host-actions">
                {listing.proveedor?.telefono && (
                  <a className="host-link" href={`tel:${listing.proveedor.telefono}`}>
                    <Phone size={16} strokeWidth={1.5} />
                    Llamar
                  </a>
                )}
                {listing.proveedor?.contacto && (
                  <a className="host-link" href={`mailto:${listing.proveedor.contacto}`}>
                    <Mail size={16} strokeWidth={1.5} />
                    Correo
                  </a>
                )}
                <Link className="host-button" to={providerHref}>
                  Contactar anfitrión
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
